import {TFunction} from 'i18next';
import {Invitation, InvitationStatus, RealEstateType} from '../types/invitation';
import {PaymentStrategy} from '../types/bank';
import {InvitationProgress, NsxSyncStatus, PaymentStatus, WebformStatus} from '../types/invitation-progress';

export enum StepState {
    Done = 'done',
    Current = 'current',
    Attention = 'attention',
    Upcoming = 'upcoming',
    Skipped = 'skipped',
}

export interface ProgressStep {
    key: string;
    title: string;
    state: StepState;
    description: string;
    // short phrase for the summary when this step is the one to look at
    summary?: string;
    // raw status as stored in the database, for support questions
    code?: string;
    // nothing for us to do: the client still has to fill in the webform
    waitingForClient?: boolean;
    // technical error details, only available for admins
    detail?: string;
}

export interface ProgressSummary {
    state: StepState;
    // the step the summary is about, undefined when everything is done
    stepKey?: string;
    waitingForClient?: boolean;
    headline: string;
    // only when the steps below don't already say it (the step that needs attention is repeated on purpose)
    text?: string;
}

const HOUR_MS = 60 * 60 * 1000;

export const formatDate = (value: string | Date): string => {
    const date = new Date(value);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}`;
};

const isOlderThan = (value: string, ms: number): boolean => Date.now() - new Date(value).getTime() > ms;

export function buildProgressSteps(invitation: Invitation, progress: InvitationProgress, t: TFunction): ProgressStep[] {
    const isCommercial = invitation.realEstateType === RealEstateType.CRE;
    const steps: ProgressStep[] = [buildInvitationStep(invitation, isCommercial, t)];
    if (!isCommercial) {
        steps.push(buildWebformStep(progress, t));
        steps.push(buildPaymentStep(invitation, progress, t));
    }
    steps.push(buildNsxStep(progress, isCommercial, t));
    steps.push(buildProcessedStep(invitation, progress, t));
    return steps;
}

export function summarize(steps: ProgressStep[], t: TFunction): ProgressSummary {
    const attention = steps.find((step) => step.state === StepState.Attention);
    if (attention) {
        return {state: StepState.Attention, stepKey: attention.key, headline: t('Needs attention'), text: attention.description};
    }
    const current = steps.find((step) => step.state === StepState.Current);
    if (current) {
        return {state: StepState.Current, stepKey: current.key, waitingForClient: current.waitingForClient, headline: current.summary ?? current.title};
    }
    const upcoming = steps.find((step) => step.state === StepState.Upcoming);
    if (upcoming) {
        return {state: StepState.Upcoming, stepKey: upcoming.key, headline: upcoming.summary ?? upcoming.title};
    }
    return {state: StepState.Done, headline: t('Done'), text: t('Everything is handled, there is nothing left to do.')};
}

function buildInvitationStep(invitation: Invitation, isCommercial: boolean, t: TFunction): ProgressStep {
    const title = isCommercial ? t('Invitation created') : t('Invitation sent');
    if (invitation.isArchived) {
        return {
            key: 'invitation',
            title,
            state: StepState.Attention,
            description: t('This invitation is archived. The client can no longer open the webform.'),
            code: invitation.status,
        };
    }
    return {
        key: 'invitation',
        title,
        state: StepState.Done,
        description: t('Created on {{date}} by {{user}}.', {date: formatDate(invitation.createdOn), user: invitation.createdBy}),
        code: invitation.status,
    };
}

function buildWebformStep(progress: InvitationProgress, t: TFunction): ProgressStep {
    const {webform, payment} = progress;
    const step = {key: 'webform', title: t('Webform filled in'), code: webform?.status};
    if (webform?.status === WebformStatus.COMPLETED) {
        return {...step, state: StepState.Done, description: t('Submitted by the client on {{date}}.', {date: formatDate(webform.updatedOn)})};
    }
    if (payment?.status === PaymentStatus.Paid && payment.paidOn) {
        // the API submits paid webforms itself when the client closes the tab before the webform client does
        return isOlderThan(payment.paidOn, HOUR_MS)
            ? {...step, state: StepState.Attention, description: t('The client paid, but the webform was never submitted. Contact Stadim support.')}
            : {
                ...step,
                state: StepState.Current,
                summary: t('Finishing the webform'),
                description: t('The client paid. The webform is submitted automatically within the hour if the client does not finish it.'),
            };
    }
    return {
        ...step,
        state: StepState.Current,
        waitingForClient: true,
        summary: t('Waiting for the client'),
        description: t('Waiting for the client to fill in and submit the webform.'),
    };
}

function buildPaymentStep(invitation: Invitation, progress: InvitationProgress, t: TFunction): ProgressStep {
    const {payment} = progress;
    const step = {key: 'payment', title: t('Payment'), code: payment?.status};
    if (invitation.paymentStrategy !== PaymentStrategy.ClientPayment) {
        return {...step, state: StepState.Skipped, description: t('Not needed, the bank pays for this valuation.')};
    }
    const attempts = payment?.unsuccessfulAttempts ?? 0;
    if (payment?.status === PaymentStatus.Paid) {
        const paid = t('Paid EUR {{amount}} on {{date}}.', {
            amount: Number(payment.amountVatInclusive ?? 0).toFixed(2),
            date: payment.paidOn ? formatDate(payment.paidOn) : '-',
        });
        const earlier = attempts > 0 ? ` ${t('{{count}} earlier attempt did not go through.', {count: attempts})}` : '';
        return {...step, state: StepState.Done, description: paid + earlier};
    }
    if (attempts > 0) {
        return {
            ...step,
            state: StepState.Current,
            summary: t('Waiting for payment'),
            description: t('The client started paying but did not finish ({{count}} unsuccessful attempt).', {count: attempts}),
        };
    }
    return {...step, state: StepState.Upcoming, summary: t('Waiting for payment'), description: t('The client pays at the end of the webform.')};
}

function buildNsxStep(progress: InvitationProgress, isCommercial: boolean, t: TFunction): ProgressStep {
    const {nsxSync, webform} = progress;
    const step = {key: 'nsx', title: t('Sent to NSX'), code: nsxSync?.status};
    if (!progress.nsxEnabled) {
        return {...step, state: StepState.Skipped, description: t('This bank does not send valuations to NSX.')};
    }
    if (!nsxSync) {
        const shouldHaveStarted = isCommercial || webform?.status === WebformStatus.COMPLETED;
        return shouldHaveStarted
            ? {...step, state: StepState.Attention, description: t('No transfer to NSX was started. Check the account case identifiers of this bank.')}
            : {...step, state: StepState.Upcoming, summary: t('Waiting for the webform'), description: t('Starts automatically once the webform is submitted.')};
    }
    switch (nsxSync.status) {
        case NsxSyncStatus.COMPLETED:
            return {...step, state: StepState.Done, description: t('Transferred on {{date}}.', {date: formatDate(nsxSync.updatedOn)})};
        case NsxSyncStatus.FAILED:
            return nsxSync.retryCount < nsxSync.maxRetryCount
                ? {
                    ...step,
                    state: StepState.Current,
                    summary: t('Retrying the transfer to NSX'),
                    description: t('The transfer failed and is retried automatically ({{count}} of {{max}} retries used).', {
                        count: nsxSync.retryCount,
                        max: nsxSync.maxRetryCount,
                    }),
                    detail: nsxSync.error,
                }
                : {
                    ...step,
                    state: StepState.Attention,
                    description: t('The transfer failed and is not retried anymore. Stadim was notified by email.'),
                    detail: nsxSync.error,
                };
        default:
            // a sync that doesn't move for an hour was interrupted, those are not retried
            return isOlderThan(nsxSync.updatedOn, HOUR_MS)
                ? {...step, state: StepState.Attention, description: t('The transfer stopped halfway and is not retried automatically. Contact Stadim support.')}
                : {...step, state: StepState.Current, summary: t('Sending to NSX'), description: t('The transfer to NSX is in progress.')};
    }
}

function buildProcessedStep(invitation: Invitation, progress: InvitationProgress, t: TFunction): ProgressStep {
    const step = {key: 'processed', title: t('Processed')};
    if (invitation.status === InvitationStatus.PROCESSED) {
        return {...step, state: StepState.Done, description: t('The valuation request is with Stadim.')};
    }
    return {
        ...step,
        state: StepState.Upcoming,
        summary: t('Waiting to be processed'),
        description: progress.nsxEnabled
            ? t('Set automatically after the transfer to NSX, or manually by an admin.')
            : t('Set manually by an admin once Stadim handles the request.'),
    };
}
